import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Alert, Button as AntdButton, Divider, Modal, Skeleton, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { CloseXIcon } from '~/img';
import colors from '~/constants/colors';
import theme from '~/constants/theme';
import { useDirection } from '~/hooks/i18nHooks';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { useGetTermsAndConditions } from '~/controllers/contract';

interface Props {
  open: boolean;
  onAgree: () => void;
  onClose: () => void;
  /** When true: hides the header title and I Agree button — read-only view (e.g. from footer) */
  readOnly?: boolean;
}

/** Static full T&C content — shown in read-only footer popup */
/** Static full T&C content in English — shown in read-only footer popup or when API fails */
const FALLBACK_HTML_EN = `
<section class="tnc">  
  <p>These terms and conditions of use (the &ldquo;Terms of Use&rdquo;) apply to the Network website located at www.network.ae, and all associated sites linked to www.network.ae by Network, its subsidiaries and affiliates, including Network sites around the world, Network application on any platform (&ldquo;Network App&rdquo;), call center, and other technology platform (collectively, the &ldquo;Network Platform&rdquo;). The Network Platform is the property of Network International LLC (&ldquo;Network&rdquo;) and/or its licensors. By using the Network Platform, you agree to these terms and conditions; if you do not agree, do not use the Network Platform. If you are a Network customer, the definitive agreement signed between you and Network shall also govern the usage of the Network Platform.</p>
  <p>Network reserves the right, at its sole discretion, to change, modify, add, or remove portions of these Terms of Use, at any time. It is your responsibility to check these Terms of Use periodically for changes. Your continued use of the Network Platform following the posting of changes will mean that you accept and agree to the changes. As long as you comply with these Terms of Use, Network grants you a personal, non-exclusive, non-transferable, limited privilege to enter and use the Network Platform on your personal device.</p>

  <h2>Content</h2>
  <p>All text, graphics, user interfaces, visual interfaces, photographs, trademarks, logos, sounds, music, artwork and computer code (collectively, &ldquo;Content&rdquo;), including but not limited to the design, structure, selection, coordination, expression, &ldquo;look and feel&rdquo; and arrangement of such Content, contained on the Network Platform is owned, controlled or licensed by or to Network, and is protected by copyright, patent and trademark laws, and various other intellectual property rights and unfair competition laws.</p>
  <p>Except as expressly provided in these Terms of Use, no part of the Network Platform and no Content may be copied, reproduced, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted or distributed in any way (including &ldquo;mirroring&rdquo;) to any other computer, server, website or other medium for publication or distribution or for any commercial enterprise, without Network&rsquo;s express prior written consent.</p>
  <p>You may use information on Network products and services (such as data sheets, knowledge base articles, and similar materials) purposely made available by Network for downloading from the Network Platform, provided that you (1) not remove any proprietary notice language in all copies of such documents, (2) use such information only for your personal, non-commercial informational purpose and do not copy or post such information on any networked computer or broadcast it in any media, (3) make no modifications to any such information and (4) not make any additional representations or warranties relating to such information.</p>

  <h2>Your Use of the Network Platform</h2>
  <p>You may not access or use the Network Platform for any purpose other than that for which we make it available. The Network Platform may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.</p>
  <p>As a user of the Network Platform, you agree not to: systematically retrieve data or other content from the Network Platform to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us; make any unauthorized use of the Network Platform, including collecting usernames and/or email addresses of users by electronic or other means for the purpose of sending unsolicited email, or creating user accounts by automated means or under false pretenses.</p>
  <p>You agree that you will not attempt to bypass, disable, or interfere with any security-related features of the Network Platform. You agree to comply with all local rules regarding acceptable online conduct and content. You agree not to use the Network Platform in any manner that could damage, disable, overburden, or impair the Network Platform, or interfere with any other party&rsquo;s use and enjoyment of the Network Platform.</p>
  <p>Company accounts (as described below) are governed by additional policies, to which you agree when you open a business new account. You are responsible for maintaining the confidentiality of your account information, including your password, and for all activity that occurs under your account. You agree to notify Network immediately of any unauthorized use of your account.</p>

  <h2>Accounts, Passwords and Security</h2>
  <p>Certain features or services offered on or through the Network Platform may require you to open an account. You are entirely responsible for maintaining the confidentiality of the information you hold for your account, including your password, and for any and all activity that occurs under your account as a result of your failing to keep this information secure and confidential.</p>
  <p>You agree to notify Network immediately of any unauthorized use of your account or password, or any other breach of security. You may be held liable for losses incurred by Network or any other user of or visitor to the Network Platform due to someone else using your password or account as a result of your failing to keep your account information secure and confidential.</p>
  <p>You may not use anyone else&rsquo;s account at any time without the permission of the account holder. Network cannot and will not be liable for any loss or damage arising from your failure to comply with these obligations.</p>

  <h2>Privacy Policy</h2>
  <p>Network&rsquo;s Privacy Notice, which is incorporated by reference into these Terms of Use, describes how we collect, use, and share information in connection with your use of our websites, services, and applications. By using the Network Platform, you also agree to the terms of our Privacy Notice.</p>

  <h2>Links to Other Websites and to the Network Platform</h2>
  <p>The Network Platform may contain links to other independent third-party websites (&ldquo;Linked Sites&rdquo;). These Linked Sites are provided solely as a convenience to our visitors. Such Linked Sites are not under Network&rsquo;s control, and Network is not responsible for and does not endorse the content of such Linked Sites, including any information or materials contained on such Linked Sites. You will need to make your own independent judgment regarding your interaction with these Linked Sites.</p>
  <p>If you would like to link to the Network Platform, you may do so only on the basis that you link to, but do not replicate, the home page of the Network Platform, and subject to the following conditions: (a) you do not remove, distort or otherwise alter the size or appearance of any Network logo; (b) you do not create a frame or any other browser or border environment around the Network Platform; (c) you do not in any way imply that Network is endorsing any products or services other than its own; (d) you do not misrepresent your relationship with Network nor present any other false information about Network; and (e) your website does not contain content that is distasteful, offensive or controversial, infringes any intellectual property rights or other rights of any other person or otherwise does not comply with all applicable laws and regulations.</p>

  <h2>Limitation of Liability</h2>
  <p>Except where prohibited by law, in no event will Network be liable to you for any indirect, consequential, exemplary, incidental or punitive damages, including lost profits, even if Network has been advised of the possibility of such damages. If Network is found liable to you for any damage or loss which arises out of or is in any way connected with your use of the Network Platform, Network&rsquo;s liability shall in no event exceed the greater of the fees you paid to Network during the month immediately preceding the loss.</p>

  <h2>Indemnity</h2>
  <p>You agree to indemnify and hold Network, its officers, directors, shareholders, predecessors, successors in interest, employees, agents, subsidiaries and affiliates, harmless from any demands, loss, liability, claims or expenses (including attorneys&rsquo; fees), made against Network by any third party due to or arising out of or in connection with your use of the Network Platform.</p>

  <h2>Violation of These Terms of Use</h2>
  <p>Network reserves the right to seek all remedies available at law and in equity for violations of these Terms of Use, including the right to block access from a particular internet address to the Network Platform and any other Network Platform.</p>
  <p>You acknowledge that Network has the right to monitor access to the Network Platform at any time and to disclose any information as Network deems necessary to satisfy any applicable law, regulation, legal process or governmental request, or to edit, refuse to post or to remove any information or materials, in whole or in part, in Network&rsquo;s sole discretion. You are also advised that Network will fully cooperate with any law enforcement authorities or court order requesting or directing Network to disclose the identity or whereabouts of anyone posting any materials in violation of these Terms of Use.</p>
  <p>Any use of the Network Platform in violation of the foregoing violates these terms and conditions and may result in, among other things, termination or suspension of your rights to use the Network Platform.</p>

  <h2>Governing Law; Disputes Resolution</h2>
  <p>You agree that all matters relating to your access to or use of the Network Platform, including all disputes, will be governed by the laws of the UAE without regard to its conflicts of laws provisions. You agree to the personal jurisdiction by and venue in the courts in Dubai, UAE, and waive any objection to such jurisdiction or venue.</p>
  <p>Any claim under these Terms of Use must be brought within one (1) year after the cause of action arises, or such claim or cause of action is barred. No recovery may be sought or received for damages other than out-of-pocket expenses, except that the prevailing party will be entitled to costs and attorneys&rsquo; fees. In the event of any controversy or dispute between Network and you arising out of or in connection with your use of the Network Platform, the parties shall attempt, promptly and in good faith, to resolve any such dispute.</p>
</section>
`;

/** Static full T&C content in Arabic — shown in read-only footer popup or when API fails */
const FALLBACK_HTML_AR = `
<section class="tnc">  
  <p>تسري شروط وأحكام الاستخدام هذه ("شروط الاستخدام") على موقع نتورك الإلكتروني الواقع على www.network.ae، وجميع المواقع المرتبطة به من قبل نتورك وشركاتها التابعة والمنتسبة، بما في ذلك مواقع نتورك حول العالم، وتطبيق نتورك على أي منصة ("تطبيق نتورك")، ومركز الاتصال، والمنصات التقنية الأخرى (يُشار إليها مجتمعة باسم "منصة نتورك"). منصة نتورك هي ملك لشركة نتورك إنترناشيونال ش.ذ.م.م ("نتورك") و/أو المرخصين لها. باستخدامك لمنصة نتورك، فإنك توافق على هذه الشروط والأحكام؛ وإذا كنت لا توافق، فلا تستخدم منصة نتورك. إذا كنت عميلاً لشركة نتورك، فإن الاتفاقية النهائية الموقعة بينك وبين نتورك تحكم أيضاً استخدام منصة نتورك.</p>
  <p>تحتفظ نتورك بالحق، وفقاً لتقديرها الخاص، في تغيير أجزاء من شروط الاستخدام هذه أو تعديلها أو إضافتها أو إزالتها في أي وقت. وتقع على عاتقك مسؤولية مراجعة شروط الاستخدام هذه بشكل دوري للاطلاع على التغييرات. إن استمرارك في استخدام منصة نتورك بعد نشر التغييرات يعني قبولك وموافقتك عليها. وطالما أنك تلتزم بشروط الاستخدام هذه، تمنحك نتورك امتيازاً شخصياً غير حصري وغير قابل للتحويل ومحدوداً للدخول إلى منصة نتورك واستخدامها على جهازك الشخصي.</p>

  <h2>المحتوى</h2>
  <p>تعتبر جميع النصوص والرسومات وواجهات المستخدم والواجهات المرئية والصور والعلامات التجارية والشعارات والأصوات والموسيقى والأعمال الفنية والرموز البرمجية (يُشار إليها مجتمعة باسم "المحتوى")، بما في ذلك على سبيل المثال لا الحصر تصميم المحتوى وهيكليته واختياره وتنسيقه والتعبير عنه ومظهره العام وترتيبه، والموجودة على منصة نتورك، مملوكة لشركة نتورك أو خاضعة لسيطرتها أو مرخصة لها، وهي محمية بقوانين حقوق الطبع والنشر وبراءات الاختراع والعلامات التجارية ومختلف حقوق الملكية الفكرية الأخرى وقوانين المنافسة غير العادلة.</p>
  <p>باستثناء ما هو منصوص عليه صراحةً في شروط الاستخدام هذه، لا يجوز نسخ أي جزء من منصة نتورك أو أي محتوى أو إعادة إنتاجه أو إعادة نشره أو تحميله أو إرساله أو عرضه علناً أو ترميزه أو ترجمته أو نقله أو توزيعه بأي طريقة (بما في ذلك "النسخ المتطابق") إلى أي كمبيوتر آخر أو خادم أو موقع ويب أو وسيط آخر للنشر أو التوزيع أو لأي مشروع تجاري دون موافقة خطية مسبقة وصريحة من نتورك.</p>
  <p>يجوز لك استخدام المعلومات الخاصة بمنتجات وخدمات نتورك (مثل أوراق البيانات ومقالات قاعدة المعرفة والمواد المماثلة) والمتاحة عمداً من قبل نتورك للتنزيل من منصة نتورك، بشرط: (1) عدم إزالة أي شعارات إشعار للملكية في جميع نسخ هذه المستندات، (2) استخدام هذه المعلومات لأغراضك الشخصية وغير التجارية فقط وعدم نسخ هذه المعلومات أو نشرها على أي كمبيوتر متصل بالشبكة أو بثها في أي وسائل إعلام، (3) عدم إجراء أي تعديلات على أي من هذه المعلومات، (4) عدم تقديم أي تعهدات أو ضمانات إضافية تتعلق بهذه المعلومات.</p>

  <h2>استخدامك لمنصة نتورك</h2>
  <p>لا يجوز لك الوصول إلى منصة نتورك أو استخدامها لأي غرض آخر غير الغرض الذي نوفرها من أجله. لا يجوز استخدام منصة نتورك في أي مساعٍ تجارية باستثناء تلك المعتمدة صراحةً من قبلنا.</p>
  <p>بصفتك مستخدماً لمنصة نتورك، فإنك توافق على عدم: استرجاع البيانات أو المحتويات الأخرى بشكل منهجي من منصة نتورك لإنشاء أو تجميع مجموعة أو قاعدة بيانات أو دليل، بشكل مباشر أو غير مباشر، دون إذن كتابي منا؛ وإجراء أي استخدام غير مصرح به لمنصة نتورك، بما في ذلك جمع أسماء المستخدمين و/أو عناوين البريد الإلكتروني للمستخدمين بوسائل إلكترونية أو غيرها لإرسال بريد إلكتروني غير مرغوب فيه، أو إنشاء حسابات مستخدمين بوسائل مؤتمتة أو تحت ذرائع كاذبة.</p>
  <p>أنت توافق على أنك لن تحاول تجاوز أو تعطيل أو التدخل في أي ميزات أمنية لمنصة نتورك. وتوافق على الامتثال لجميع القواعد المحلية المتعلقة بالسلوك والمحتوى المقبول عبر الإنترنت. كما توافق على عدم استخدام منصة نتورك بأي طريقة قد تؤدي إلى إتلاف منصة نتورك أو تعطيلها أو زيادة العبء عليها أو إضعافها، أو التدخل في استخدام أي طرف آخر لمنصة نتورك وتمتعه بها.</p>
  <p>تخضع حسابات الشركات (كما هو موضح أدناه) لسياسات إضافية، وتوافق عليها عند فتح حساب عمل جديد. أنت مسؤول عن الحفاظ على سرية معلومات حسابك، بما في ذلك كلمة المرور الخاصة بك، وعن جميع الأنشطة التي تحدث تحت حسابك. وتوافق على إخطار نتورك فوراً بأي استخدام غير مصرح به لحسابك.</p>

  <h2>الحسابات وكلمات المرور والأمن</h2>
  <p>قد تتطلب بعض الميزات أو الخدمات المقدمة على منصة نتورك أو من خلالها فتح حساب. وتتحمل المسؤولية الكاملة عن الحفاظ على سرية المعلومات التي تحتفظ بها لحسابك، بما في ذلك كلمة المرور الخاصة بك، وعن أي نشاط يحدث تحت حسابك نتيجة لإخفاقك في الحفاظ على أمن وسرية هذه المعلومات.</p>
  <p>توافق على إخطار نتورك فوراً بأي استخدام غير مصرح به لحسابك أو كلمة مرورك، أو أي خرق أمني آخر. وقد تتحمل المسؤولية عن الخسائر التي تلحق بنتورك أو أي مستخدم آخر أو زائر لمنصة نتورك بسبب استخدام شخص آخر لكلمة مرورك أو حسابك نتيجة لإخفاقك في الحفاظ على أمن وسرية معلومات حسابك.</p>
  <p>لا يجوز لك استخدام حساب أي شخص آخر في أي وقت دون إذن صريح من صاحب الحساب. لا يمكن لنتورك ولن تكون مسؤولة عن أي خسارة أو ضرر ينشأ عن إخفاقك في الامتثال لهذه الالتزامات.</p>

  <h2>سياسة الخصوصية</h2>
  <p>تصف سياسة الخصوصية الخاصة بنتورك، والمدمجة في شروط الاستخدام هذه بالإحالة إليها، كيفية جمعنا لمعلوماتك واستخدامها ومشاركتها فيما يتعلق باستخدامك لمواقعنا وخدماتنا وتطبيقاتنا. باستخدامك لمنصة نتورك، فإنك توافق أيضاً على شروط سياسة الخصوصية الخاصة بنا.</p>

  <h2>الروابط المؤدية إلى مواقع أخرى وإلى منصة نتورك</h2>
  <p>قد تحتوي منصة نتورك على روابط لمواقع ويب خارجية مستقلة ("المواقع المرتبطة"). يتم توفير هذه المواقع المرتبطة فقط لراحة زوارنا. ولا تخضع هذه المواقع لسيطرة نتورك، ونتورك غير مسؤولة عن محتوى هذه المواقع ولا تصادق عليه، بما في ذلك أي معلومات أو مواد تحتوي عليها. ستحتاج إلى اتخاذ قرارك المستقل بشأن تفاعلك مع هذه المواقع المرتبطة.</p>
  <p>إذا كنت ترغب في الارتباط بمنصة نتورك، فلا يجوز لك القيام بذلك إلا على أساس ربط الصفحة الرئيسية لمنصة نتورك دون نسخها، وتخضع للشروط التالية: (أ) ألا تزيل شعار نتورك أو تشوهه أو تغير حجمه أو مظهره بأي شكل آخر؛ (ب) ألا تنشئ إطاراً أو أي بيئة متصفح أو حدود أخرى حول منصة نتورك؛ (ج) ألا تلمح بأي شكل من الأشكال إلى أن نتورك تصادق على أي منتجات أو خدمات أخرى غير منتجاتها وخدماتها؛ (د) ألا تسئ تمثيل علاقتك مع نتورك ولا تقدم أي معلومات كاذبة أخرى عنها؛ (هـ) ألا يحتوي موقع الويب الخاص بك على محتوى بغيض أو مسيئ أو مثير للجدل، أو ينتهك أي حقوق ملكية فكرية أو حقوق أخرى لأي شخص آخر، أو لا يتوافق مع القوانين والأنظمة المعمول بها.</p>

  <h2>تحديد المسؤولية</h2>
  <p>باستثناء ما يحظره القانون، لن تكون نتورك مسؤولة تجاهك بأي حال من الأحوال عن أي أضرار غير مباشرة أو تبعية أو تحذيرية أو عرضية أو تأديبية، بما في ذلك الأرباح الضائعة، حتى لو تم إخطار نتورك بإمكانية حدوث مثل هذه الأضرار. إذا تبين أن نتورك مسؤولة تجاهك عن أي ضرر أو خسارة تنشأ عن استخدامك لمنصة نتورك أو ترتبط بها بأي شكل من الأشكال، فإن مسؤولية نتورك لا تتجاوز بأي حال من الأحوال الرسوم التي دفعتها لنتورك خلال الشهر السابق مباشرة للخسارة.</p>

  <h2>التعويض</h2>
  <p>أنت توافق على تعويض نتورك ومسؤوليها ومديريها ومساهميها وأسلافهم وخلفائهم في المنصب وموظفيها ووكلائها وشركاتها التابعة والمنتسبة وحمايتهم من أي مطالبات أو خسائر أو مسؤوليات أو نفقات (بما في ذلك أتعاب المحاماة) يقدمها أي طرف ثالث ضد نتورك نتيجة لاستخدامك لمنصة نتورك أو فيما يتعلق بهذا الاستخدام.</p>

  <h2>مخالفة شروط الاستخدام هذه</h2>
  <p>تحتفظ نتورك بالحق في السعي للحصول على جميع التدابير المتاحة قانوناً وإنصافاً لمخالفات شروط الاستخدام هذه، بما في ذلك الحق في حظر الوصول من عنوان إنترنت معين إلى منصة نتورك وأي منصات أخرى تابعة لنتورك.</p>
  <p>أنت تقر بأن لنتورك الحق في مراقبة الوصول إلى منصة نتورك في أي وقت والكشف عن أي معلومات تراها ضرورية لتلبية أي قانون أو نظام معمول به أو إجراء قانوني أو طلب حكومي، أو تعديل أي معلومات أو مواد أو رفض نشرها أو إزالتها كلياً أو جزئياً وفقاً لتقدير نتورك الخاص. كما يُرجى العلم بأن نتورك ستتعاون بشكل كامل مع أي سلطات لإنفاذ القانون أو أمر قضائي يطلب من نتورك الكشف عن هوية أو مكان أي شخص ينشر أي مواد تنتهك شروط الاستخدام هذه.</p>
  <p>أي استخدام لمنصة نتورك ينتهك ما تقدم يعتبر انتهاكاً لهذه الشروط والأحكام وقد يؤدي، من بين أمور أخرى، إلى إنهاء أو تعليق حقوقك في استخدام منصة نتورك.</p>

  <h2>القانون الواجب التطبيق؛ تسوية النزاعات</h2>
  <p>أنت توافق على أن جميع المسائل المتعلقة بالوصول إلى منصة نتورك أو استخدامها، بما في ذلك جميع النزاعات، ستخضع لقوانين دولة الإمارات العربية المتحدة دون اعتبار لأحكام تنازع القوانين. وتوافق على الاختصاص القضائي والمكان في المحاكم بدبي، دولة الإمارات العربية المتحدة، وتتنازل عن أي اعتراض على هذا الاختصاص القضائي أو المكان.</p>
  <p>أي مطالبة بموجب شروط الاستخدام هذه يجب تقديمها في غضون سنة واحدة (1) بعد نشوء سبب الدعوى، وإلا فإن هذه المطالبة أو سبب الدعوى يسقط بالتقادم. ولا يجوز السعي للحصول على تعويض عن أضرار أخرى غير النفقات الفعلية، باستثناء أن الطرف الغالب يحق له الحصول على التكاليف وأتعاب المحاماة. وفي حالة حدوث أي خلاف أو نزاع بين نتورك وبينك ينشأ عن استخدامك لمنصة نتورك أو يتعلق به، يتعين على الأطراف محاولة حل هذا النزاع فوراً وبحسن نية.</p>
</section>
`;


const TermsAndConditionsModal: FC<Props> = ({ open, onAgree, onClose, readOnly = false }) => {
  const { t } = useTranslation('ksaApplication');
  const dir = useDirection();
  const isMobile = useIsMobile();

  const fallbackHtml = dir === 'rtl' ? FALLBACK_HTML_AR : FALLBACK_HTML_EN;

  // enabled: !readOnly — hook always called (Rules of Hooks), API only fires when NOT readOnly
  const tcQuery = useGetTermsAndConditions({ enabled: !readOnly });

  const renderBody = () => {
    // When readOnly (footer popup), skip API and show static content directly
    if (readOnly) {
      return (
        <HtmlContent
          dir={dir}
          dangerouslySetInnerHTML={{ __html: fallbackHtml }}
        />
      );
    }

    if (tcQuery.isLoading) {
      return <Skeleton active paragraph={{ rows: isMobile ? 6 : 10 }} />;
    }

    if (tcQuery.isError) {
      return (
        <>
          <Alert
            type="warning"
            message={t('Could not load Terms & Conditions from server. Showing default content.')}
            style={{ marginBottom: 12, fontSize: 12 }}
          />
          <HtmlContent
            dir={dir}
            dangerouslySetInnerHTML={{ __html: fallbackHtml }}
          />
          <RetryBtn
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => {
              void tcQuery.refetch();
            }}
            loading={tcQuery.isFetching}
          >
            {t('Retry')}
          </RetryBtn>
        </>
      );
    }

    return (
      <HtmlContent
        dir={dir}
        dangerouslySetInnerHTML={{ __html: tcQuery.data?.contentHtml ?? '' }}
      />
    );
  };

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      isMobile={isMobile}
      title={
        <ModalTitleRow isMobile={isMobile} dir={dir}>
          <Typography.Text className={readOnly ? 'ModalTitle-Text ModalTitle-Text--readOnly' : 'ModalTitle-Text'}>
            {readOnly
              ? t('Terms and Conditions of Use. Ownership of Network Platform')
              : t('Terms & Conditions')}
          </Typography.Text>
          <CloseBtn onClick={onClose} id="termsModal_closeBtn">
            <CloseXIcon />
          </CloseBtn>
        </ModalTitleRow>
      }
      centered={!isMobile}
      /* On mobile: full-width, anchored to bottom via CSS.
         On desktop: fixed 760px centered modal */
      width={isMobile ? '100vw' : 920}
      style={isMobile ? { top: 'auto', bottom: 0, margin: 0, paddingBottom: 0, maxWidth: '100vw' } : undefined}
      bodyStyle={{
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <BodyContent isMobile={isMobile} dir={dir}>{renderBody()}</BodyContent>

      {!readOnly && (
        <>
          <FooterArea isMobile={isMobile}>
            <AgreeButton
              id="termsModal_agreeButton"
              type="primary"
              block
              onClick={onAgree}
              disabled={tcQuery.isLoading}
            >
              {t('I Agree')}
            </AgreeButton>
          </FooterArea>
        </>
      )}
    </StyledModal>
  );
};

/* ─── Styled components ─────────────────────────────────────── */

interface ResponsiveProps {
  isMobile?: boolean;
  dir?: string;
}

const StyledModal = styled(Modal) <ResponsiveProps>`
  .ant-modal-header {
    padding: ${({ isMobile }) => (isMobile ? '26px 24px 0px 24px' : '40px 40px 30px 40px')};
    border-bottom: none;
    border-radius: ${({ isMobile }) => (isMobile ? '20px 20px 0 0' : '16px')} !important;
  }
  .ant-modal-body {
    padding:${({ isMobile }) => (isMobile ? '16px 24px 32px 24px' : '0px 40px 30px 40px')} !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: ${({ isMobile }) => (isMobile ? '80vh' : '70vh')};
  }

  /* Mobile: stick to bottom of screen */
  @media (max-width: ${theme.breakpoints.sm}px) {
    position: fixed !important;
    bottom: 0 !important;
    top: auto !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 !important;
    padding-bottom: 0 !important;
    max-width: 100vw !important;

    .ant-modal-content {
      border-radius: 20px 20px 0 0;
    }
  }
`;

const ModalTitleRow = styled.div<ResponsiveProps>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  .ModalTitle-Text {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size:24px;
    font-weight: 600;
    color: #1a1a2e;
    flex: 1;
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  }

  /* Read-only variant: larger title matching the T&C heading style */
  .ModalTitle-Text--readOnly {
    font-size: ${({ isMobile }) => (isMobile ? '18px' : '22px')};
    font-weight: 700;
    line-height: 1.3;
    white-space: normal;
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  &:hover {
    // background: #f5f5f5;
  }
`;

const BodyContent = styled.div<ResponsiveProps>`
  padding: ${({ isMobile }) => (isMobile ? '6px 0' : '8px 0')};
  text-align: ${({ dir }) => dir === 'rtl' ? 'right' : ""};
  flex: 1;
  overflow-y: auto;
  direction: ${({ dir }) => dir ?? 'ltr'};
`;

/**
 * Scoped styles for the raw HTML returned by the T&C API.
 * All selectors are scoped under .tnc to avoid leaking into the rest of the UI.
 */
const HtmlContent = styled.div<{ dir?: string }>`
  direction: ${({ dir }) => dir ?? 'ltr'};

  /* ── headings ── */
  .tnc h1 {
    font-family: Inter, Tajawal, sans-serif !important;
    font-size: 28px !important;
    font-weight: 700 !important;
    color: #1a1a2e !important;
    margin: 0 0 20px !important;
    line-height: 36px !important;
    letter-spacing: -0.3px !important;
  }
  .tnc h2 {
    font-family: Inter, Tajawal, sans-serif !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    color: #1a1a2e !important;
    margin: 16px 0 8px !important;
  }
  .tnc h3 {
    font-family: Inter, Tajawal, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 12px 0 6px;
  }

  /* ── paragraphs ── */
  .tnc p {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 13px;
    line-height: 22px;
    color: #3d3d3d;
    margin: 0 0 12px;

    @media (max-width: ${theme.breakpoints.sm}px) {
      font-size: 12px;
      line-height: 20px;
    }
  }

  /* ── lists ── */
  .tnc ul,
  .tnc ol {
    padding-inline-start: 20px;
    margin: 0 0 12px;
  }
  .tnc li {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 13px;
    line-height: 22px;
    color: #3d3d3d;
    margin-bottom: 4px;

    @media (max-width: ${theme.breakpoints.sm}px) {
      font-size: 12px;
    }
  }

  /* ── links ── */
  .tnc a {
    color: ${colors.primaryColor};
    text-decoration: underline;
    word-break: break-all;
  }

  /* ── strong / em ── */
  .tnc strong,
  .tnc b {
    font-weight: 700;
    color: #1a1a2e;
  }
  .tnc em,
  .tnc i {
    font-style: italic;
  }

  /* ── horizontal rule ── */
  .tnc hr {
    border: none;
    border-top: 1px solid #f0f0f0;
    margin: 16px 0;
  }
`;

const FooterArea = styled.div<ResponsiveProps>`
  padding: ${({ isMobile }) => (isMobile ? '16px 0px 0px 0px' : '30px 0px 0px 0px')};
  flex-shrink: 0;
  @media (max-width: ${theme.breakpoints.sm}px) {
    background: #FFF;
    box-shadow: 0 -2px 8px -2px var(--Shadow-boxShadow, rgba(0, 89, 150, 0.03)), 0 -8px 8px -2px var(--Shadow-boxShadow, rgba(0, 89, 150, 0.03));   
  }
`;

/* Uses AntdButton directly — no mandatory id requirement */
const RetryBtn = styled(AntdButton)`
  padding: 0;
  font-size: 12px;
  color: ${colors.primaryColor};
`;

/* Uses AntdButton directly — no mandatory id requirement */
const AgreeButton = styled(AntdButton)`
  height: 52px !important;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  border-radius: var(--SM, 8px);
border: 1px solid var(--colorPrimary-colorPrimary, #0069B1);
background: var(--colorPrimary-colorPrimary, #0069B1);

/* shadow/sm */
  @media (max-width: ${theme.breakpoints.sm}px) {
    height: 48px!important;
    font-size: 14px;    
  }
`;

export default TermsAndConditionsModal;
